/**
 * ISAYA product page — overlay panel component (size guide + notify me).
 *
 * One panel component, opened from two places:
 *   1. a "SIZE GUIDE" link injected onto the size row, right-aligned opposite the SIZE label
 *   2. the primary buy button, when no purchasable variant is selected
 *
 * The notify panel always asks for an email address, because an address is always worth having.
 * The back-in-stock app injects a hook element on the products its campaign covers; where that
 * hook is there, the button hands the click straight to the app and this panel never opens.
 * Everywhere else — a product the campaign does not reach, or the app failing to load — the
 * panel takes the address itself and posts it to Shopify's own customer list, tagged with the
 * product handle so ISAYA can see which sold-out piece the demand was for.
 *
 * The panels are native <dialog> elements, so Escape, the focus trap and focus return to the
 * trigger are the platform's. Outside-click is handled here. The page behind stays visible.
 *
 * Nothing in this file touches theme markup permanently: it adds a class and one element to the
 * size row, and swaps which button is visible in the buy-button slot. Horizon re-renders the
 * product details on every variant change, so everything re-applies on `variant:update`, with a
 * MutationObserver as a safety net.
 */
(function () {
  'use strict';

  var LINK_CLASS = 'isaya-size-guide-link';
  var SIZE_ROW_CLASS = 'isaya-size-row';
  var NOTIFY_BUTTON_CLASS = 'isaya-notify-button';
  var HIDDEN_CLASS = 'isaya-notify-hidden';

  /* The back-in-stock app marks the products its campaign covers by injecting this container,
     and its own signup trigger lives inside it. It is injected asynchronously and re-injected
     when Horizon swaps the variant, which is why this is read on every pass rather than once
     at load. */
  var BIS_HOOK_SELECTOR = '.essential-preorder-bis-container-active';

  var applying = false;

  /* ------------------------------------------------------------------ *
   * Where the address goes.
   *
   * Shopify's own customer endpoint — the same one the footer signup and the newsletter band
   * use. No app, no key, no monthly cost, and the address lands in the list Shopify Email
   * already sends from, tagged with the product so ISAYA can see what the demand was for.
   *
   * `window.ISAYA_NOTIFY_HANDLER` still wins if something defines it, so a dedicated
   * back-in-stock backend can be dropped in later without touching this file.
   *
   *   payload = { email, productHandle, productTitle, size }
   * ------------------------------------------------------------------ */
  function submitNotifyRequest(payload) {
    if (typeof window.ISAYA_NOTIFY_HANDLER === 'function') {
      return window.ISAYA_NOTIFY_HANDLER(payload);
    }
    return postToCustomerList(payload);
  }

  /* The storefront's `customer` form, submitted from script instead of by a page reload, so the
     panel can answer in place. Same three fields the theme's own signup sends, plus the tags. */
  function postToCustomerList(payload) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var body = new URLSearchParams();
    body.append('form_type', 'customer');
    body.append('utf8', '✓');
    body.append('contact[email]', payload.email);
    if (payload.productHandle) {
      body.append('contact[tags]', 'back-in-stock, ' + payload.productHandle);
    }

    return fetch(root + 'contact', {
      method: 'POST',
      body: body,
      headers: { Accept: 'text/html' }
    }).then(function (response) {
      /* An address already on the list comes back as a form error rather than a redirect, and
         that person is subscribed either way — which is the outcome we wanted. So the only
         real failure left is the request not arriving, and that rejects before reaching here. */
      if (response.status >= 500) {
        throw new Error('signup unavailable');
      }
      return true;
    });
  }

  /* Shown only if the request never lands. Every word is from the signed
     size-guide line: "WRITE TO ISAYA@ISAYA-STORE.COM ... AND WE WILL HELP." */
  var FALLBACK_STATUS = 'WRITE TO ISAYA@ISAYA-STORE.COM AND WE WILL HELP.';

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  function detailsRootFor(panels) {
    return panels.closest('.product-details') || panels.closest('.shopify-section') || document;
  }

  /* ---------------- size guide ---------------- */

  function sizeRowIn(root, optionName) {
    if (!optionName) return null;
    var wanted = optionName.trim().toUpperCase();
    var found = null;
    each(root.querySelectorAll('.variant-option--dropdowns'), function (row) {
      if (found) return;
      var label = row.querySelector('label');
      var select = row.querySelector('select');
      var name = label ? label.textContent.trim().toUpperCase() : '';
      if (!name && select) {
        name = (select.getAttribute('name') || '').replace(/^options\[|\]$/g, '').trim().toUpperCase();
      }
      if (name === wanted) found = row;
    });
    return found;
  }

  function applySizeGuide(panels, root) {
    var dialogId = panels.getAttribute('data-size-guide-id');
    var dialog = dialogId ? document.getElementById(dialogId) : null;
    if (!dialog) return;

    var row = sizeRowIn(root, panels.getAttribute('data-size-option-name'));
    if (!row) return;
    if (row.querySelector('.' + LINK_CLASS)) return;

    var link = document.createElement('button');
    link.type = 'button';
    link.className = LINK_CLASS;
    link.textContent = panels.getAttribute('data-size-guide-label') || 'SIZE GUIDE';
    link.setAttribute('aria-haspopup', 'dialog');
    link.addEventListener('click', function () {
      openPanel(dialog, link);
    });

    var label = row.querySelector('label');
    row.classList.add(SIZE_ROW_CLASS);
    if (label && label.nextSibling) {
      row.insertBefore(link, label.nextSibling);
    } else {
      row.appendChild(link);
    }
  }

  /* ---------------- notify me ---------------- */

  function selectedSize(root, optionName) {
    var row = sizeRowIn(root, optionName);
    if (!row) return '';
    var select = row.querySelector('select');
    if (select && select.value) return select.value.trim();
    var checked = row.querySelector('input[type="radio"]:checked');
    return checked ? (checked.value || '').trim() : '';
  }

  function notifyDialogFor(panels) {
    var dialogId = panels.getAttribute('data-notify-id');
    return dialogId ? document.getElementById(dialogId) : null;
  }

  /* The app's own signup trigger, when the campaign covers this product. Read on every click
     rather than cached: Horizon re-renders the product details on variant change, and the app
     re-injects this element each time. */
  function appNotifyTrigger() {
    var hook = document.querySelector(BIS_HOOK_SELECTOR);
    if (!hook) return null;
    return hook.querySelector('[data-essential-preorder-element="back-in-stock-button"]');
  }

  /* The second half is not decoration. Shopify's customer form subscribes the address to email
     marketing — that is what makes it the newsletter form — so the panel says so before the
     button is pressed, the same way the footer signup and the pop-up do. */
  function notifyCopy(panels, root) {
    var title = (panels.getAttribute('data-product-title') || '').toUpperCase();
    var size = selectedSize(root, panels.getAttribute('data-size-option-name')).toUpperCase();
    var line = size
      ? 'WE WILL WRITE TO YOU ONCE THE ' + title + ' IS BACK IN SIZE ' + size
      : 'WE WILL WRITE TO YOU ONCE THE ' + title + ' IS BACK IN STOCK';
    return line + ', AND WHEN NEW PIECES LAND.';
  }

  /* Keeps the copy in step with the variant on screen. There is one mode now — the field is
     always offered — so nothing here decides what to show, only what it should say.

     It must not touch the status line. The observer below re-runs this on every DOM change, and
     writing a confirmation into the panel is itself a DOM change, so clearing here would wipe
     the confirmation the moment it appeared. A fresh open clears it instead. */
  function applyNotifyMode(panels, root) {
    var dialog = notifyDialogFor(panels);
    if (!dialog) return;

    var copy = dialog.querySelector('[data-isaya-notify-copy]');
    if (copy) copy.textContent = notifyCopy(panels, root);
  }

  function applyNotify(panels, root) {
    var dialogId = panels.getAttribute('data-notify-id');
    var dialog = dialogId ? document.getElementById(dialogId) : null;
    if (!dialog) return;

    var addToCart = root.querySelector('add-to-cart-component');
    if (!addToCart) return;
    var realButton = addToCart.querySelector('button[name="add"]');
    if (!realButton) return;

    var soldOut = realButton.disabled;
    var slot = addToCart.parentNode;
    if (!slot) return;

    var button = slot.querySelector('.' + NOTIFY_BUTTON_CLASS);

    if (!soldOut) {
      if (button) button.remove();
      addToCart.classList.remove(HIDDEN_CLASS);
      return;
    }

    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'button ' + NOTIFY_BUTTON_CLASS;
      button.setAttribute('aria-haspopup', 'dialog');
      button.addEventListener('click', function () {
        /* The app owns the signup. Essential Preorder renders its own hidden trigger inside
           BIS_HOOK_SELECTOR, and clicking it opens the app's "Notify me" card — which names the
           product and size, stores the address, and sends the restock email. Handing the click
           straight to it is why nothing here has to know the app's endpoint or payload, and why
           an app update cannot silently break the signup. Our own panel stays as the fallback
           for any product the campaign does not cover. */
        var appTrigger = appNotifyTrigger();
        if (appTrigger) {
          appTrigger.click();
          return;
        }

        /* A fresh open starts clean — last time's confirmation is not about this visit. */
        var status = dialog.querySelector('[data-isaya-notify-status]');
        if (status) {
          status.hidden = true;
          status.textContent = '';
        }
        applyNotifyMode(panels, root);
        openPanel(dialog, button);
        var input = dialog.querySelector('.isaya-notify-form__input');
        if (input) input.focus();
      });
      slot.insertBefore(button, addToCart.nextSibling);
    }

    button.textContent = panels.getAttribute('data-notify-label') || 'NOTIFY ME WHEN BACK';
    addToCart.classList.add(HIDDEN_CLASS);
  }

  function bindNotifyForm(panels) {
    var dialogId = panels.getAttribute('data-notify-id');
    var dialog = dialogId ? document.getElementById(dialogId) : null;
    if (!dialog || dialog.hasAttribute('data-isaya-bound')) return;
    dialog.setAttribute('data-isaya-bound', '');

    var form = dialog.querySelector('[data-isaya-notify-form]');
    var status = dialog.querySelector('[data-isaya-notify-status]');
    if (!form) return;

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var input = form.querySelector('.isaya-notify-form__input');
      var email = input ? input.value.trim() : '';

      if (!email || email.indexOf('@') < 1) {
        if (input) input.focus();
        return;
      }

      var root = detailsRootFor(panels);
      var result = null;
      try {
        result = submitNotifyRequest({
          email: email,
          productHandle: panels.getAttribute('data-product-handle') || '',
          productTitle: panels.getAttribute('data-product-title') || '',
          size: selectedSize(root, panels.getAttribute('data-size-option-name'))
        });
      } catch (error) {
        result = null;
      }

      if (result && typeof result.then === 'function') {
        result.then(
          function () {
            showStatus(status, 'WE WILL WRITE TO YOU WHEN IT IS BACK.');
            form.reset();
          },
          function () {
            showStatus(status, FALLBACK_STATUS);
          }
        );
        return;
      }

      showStatus(status, FALLBACK_STATUS);
    });
  }

  function showStatus(status, text) {
    if (!status) return;
    status.textContent = text;
    status.hidden = false;
  }

  /* ---------------- panel open / close ---------------- */

  function openPanel(dialog, trigger) {
    if (dialog.open) return;
    dialog.isayaTrigger = trigger || document.activeElement;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  function bindPanel(dialog) {
    if (dialog.hasAttribute('data-isaya-panel-bound')) return;
    dialog.setAttribute('data-isaya-panel-bound', '');

    each(dialog.querySelectorAll('[data-isaya-close]'), function (button) {
      button.addEventListener('click', function () {
        dialog.close();
      });
    });

    /* Outside click: a click on the backdrop lands on the dialog element itself. */
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });

    /* Focus returns to whatever opened the panel, however it was closed. */
    dialog.addEventListener('close', function () {
      var trigger = dialog.isayaTrigger;
      if (trigger && trigger.isConnected && typeof trigger.focus === 'function') trigger.focus();
    });
  }

  /* ---------------- wiring ---------------- */

  function apply() {
    if (applying) return;
    applying = true;
    try {
      each(document.querySelectorAll('isaya-product-panels'), function (panels) {
        var root = detailsRootFor(panels);
        each(panels.querySelectorAll('dialog.isaya-panel'), bindPanel);
        bindNotifyForm(panels);
        applySizeGuide(panels, root);
        applyNotify(panels, root);
        /* Runs on every pass — including the ones the body observer schedules — so the panel
           heals itself as soon as the app's hook shows up. */
        applyNotifyMode(panels, root);
      });
    } finally {
      applying = false;
    }
  }

  var pending = false;

  function scheduleApply() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        pending = false;
        apply();
      });
    });
  }

  /* Horizon swaps the whole product-details subtree on a variant change, and the swap can land
     after the variant:update listeners have run. Re-apply a few times to catch it whenever it
     lands; apply() is idempotent, so extra passes are free. */
  function applyAfterVariantChange() {
    scheduleApply();
    setTimeout(apply, 120);
    setTimeout(apply, 400);
    setTimeout(apply, 900);
  }

  function start() {
    apply();

    if (!('MutationObserver' in window)) return;
    /* Observe the body, not the details column: that column is one of the nodes being replaced,
       so an observer bound to it goes stale after the first variant change. */
    var observer = new MutationObserver(function () {
      if (applying) return;
      scheduleApply();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('variant:update', applyAfterVariantChange);
})();
